import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';
import { useLang } from '../LangContext';
import { mediaAlt } from '../lib/mediaSeo';
import HomeBlueprintPage from './HomeBlueprintPage';
import { HOME_BLUEPRINT_TEMPLATE } from '../pageBlueprint';
import { LAYOUT_EDITOR_TEMPLATE, normalizePageBlocks } from '../pageLayout';

export default function CustomPage({ fallback = null }) {
  const { lang, overlay, contentReady } = useLang();
  const location = useLocation();
  const path = normalizeCustomPath(location.pathname);
  const page = findPublishedCustomPage(overlay, path);

  if (!page) return contentReady ? fallback : <RouteLoadingFallback />;

  if (page.template === HOME_BLUEPRINT_TEMPLATE) {
    return <HomeBlueprintPage page={page} />;
  }
  if (page.template === LAYOUT_EDITOR_TEMPLATE) {
    return <LayoutEditorPage page={page} />;
  }

  const localized = page.translations?.[lang] || page.translations?.nl || {};
  const seo = page.seo?.[lang] || {};
  const title = localized.title || page.title || path.replace(/^\//, '');
  const body = localized.body || '';
  const excerpt = localized.excerpt || seo.description || '';
  const image = localized.image || seo.image || page.image || '';
  const hasHtmlBody = /<\/?[a-z][\s\S]*>/i.test(body);

  return (
    <article data-testid="custom-page" className="relative bg-[#FAF8F2] min-h-[70vh]">
      <Seo
        title={seo.title || title}
        description={seo.description || excerpt}
        path={path}
        image={seo.image || image || undefined}
        lang={lang}
        noindex={seo.noindex === true}
      />

      <section className="pt-28 sm:pt-36 pb-16 lg:pb-24">
        <div className="max-w-[1120px] mx-auto px-5 lg:px-10">
          <nav className="text-[12px] text-[#5C5852] mb-8">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="px-2 opacity-50">/</span>
            <span className="text-[#1F1D1A]">{title}</span>
          </nav>

          {image && (
            <div className="relative rounded-[22px] overflow-hidden aspect-[16/7] bg-white border border-[#E5E0D2] mb-10">
              <img src={image} alt={mediaAlt(overlay, image, lang, title)} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          )}

          <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-medium mb-5">
            {localized.eyebrow || (lang === 'de' ? 'Information' : 'Informatie')}
          </p>
          <h1 className="font-display text-[#1F1D1A] text-[40px] sm:text-[64px] lg:text-[84px] leading-[1.04] max-w-4xl">
            {title}
          </h1>
          {excerpt && <p className="mt-7 text-[#5C5852] text-[17px] lg:text-[19px] leading-relaxed max-w-3xl">{excerpt}</p>}

          {hasHtmlBody ? (
            <div className="mt-10 prose-like max-w-3xl text-[#1F1D1A] text-[16px] leading-[1.85]" dangerouslySetInnerHTML={{ __html: body }} />
          ) : (
            <div className="mt-10 prose-like max-w-3xl">
              {body.split(/\n{2,}/).filter(Boolean).map((paragraph, idx) => (
                <p key={idx} className="text-[#1F1D1A] text-[16px] leading-[1.85] mb-5 whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </div>
          )}

          <Link to="/offerte" className="pill pill-leaf mt-8 inline-flex">
            {lang === 'de' ? 'Angebot anfragen' : 'Offerte aanvragen'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </article>
  );
}

export function RouteLoadingFallback() {
  return (
    <div
      data-testid="route-loading"
      className="bg-[#FAF8F2] min-h-[70vh]"
      aria-hidden="true"
    />
  );
}

function LayoutEditorPage({ page }) {
  const { lang, overlay } = useLang();
  const path = normalizePath(page.path || page.slug);
  const localized = page.translations?.[lang] || page.translations?.nl || {};
  const seo = page.seo?.[lang] || {};
  const title = localized.title || path.replace(/^\//, '');
  const excerpt = localized.excerpt || seo.description || '';
  const blocks = normalizePageBlocks(page.blocks).filter((block) => block.enabled !== false);
  const hero = blocks.find((block) => block.type === 'hero');
  const heroText = hero?.translations?.[lang] || hero?.translations?.nl || {};
  const rest = hero ? blocks.filter((block) => block.id !== hero.id) : blocks;

  return (
    <article data-testid="layout-editor-page" className="relative bg-[#FAF8F2] min-h-[70vh]">
      <Seo
        title={seo.title || title}
        description={seo.description || excerpt || heroText.subtitle}
        path={path}
        image={seo.image || heroText.image || undefined}
        lang={lang}
        noindex={seo.noindex === true}
      />

      <section className="relative overflow-hidden pt-28 sm:pt-36 pb-14 lg:pb-20 bg-[#1F1D1A] text-white">
        {heroText.image && (
          <img src={heroText.image} alt={mediaAlt(overlay, heroText.image, lang, heroText.title || title)} className="absolute inset-0 w-full h-full object-cover opacity-45" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1F1D1A]/90 via-[#1F1D1A]/62 to-[#1F1D1A]/20" />
        <div className="relative max-w-[1240px] mx-auto px-5 lg:px-10">
          <nav className="text-[12px] text-white/65 mb-8">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="px-2 opacity-50">/</span>
            <span>{title}</span>
          </nav>
          <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F8B37A] font-medium mb-5">
            {heroText.eyebrow || localized.eyebrow || 'Horrenmaker'}
          </p>
          <h1 className="font-display text-[42px] sm:text-[68px] lg:text-[92px] leading-[1.02] max-w-4xl">
            {heroText.title || title}
          </h1>
          {(heroText.subtitle || excerpt) && (
            <p className="mt-7 text-white/78 text-[17px] lg:text-[20px] leading-relaxed max-w-2xl">{heroText.subtitle || excerpt}</p>
          )}
          {heroText.button_text && (
            <Link to={heroText.button_link || '/offerte'} className="pill pill-leaf mt-9 inline-flex">
              {heroText.button_text} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </section>

      <div className="py-14 lg:py-20 space-y-14">
        {rest.map((block) => (
          <PageBlock key={block.id} block={block} lang={lang} />
        ))}
      </div>
    </article>
  );
}

function PageBlock({ block, lang }) {
  const { overlay } = useLang();
  const tr = block.translations?.[lang] || block.translations?.nl || {};
  if (block.type === 'text') {
    return (
      <section className="max-w-[980px] mx-auto px-5 lg:px-10">
        {tr.title && <h2 className="font-display text-[#1F1D1A] text-[34px] lg:text-[54px] leading-tight mb-7">{tr.title}</h2>}
        <div className="prose-like text-[#1F1D1A] text-[16px] lg:text-[17px] leading-[1.85]" dangerouslySetInnerHTML={{ __html: tr.html || '' }} />
      </section>
    );
  }
  if (block.type === 'image_text') {
    const mediaFirst = block.layout === 'media-left';
    const media = (
      <div className="relative rounded-[22px] overflow-hidden aspect-[4/3] bg-white border border-[#E5E0D2]">
        {tr.image ? <img src={tr.image} alt={mediaAlt(overlay, tr.image, lang, tr.alt || tr.title || '')} className="absolute inset-0 w-full h-full object-cover" /> : null}
      </div>
    );
    const text = (
      <div>
        {tr.eyebrow && <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-medium mb-4">{tr.eyebrow}</p>}
        {tr.title && <h2 className="font-display text-[#1F1D1A] text-[34px] lg:text-[52px] leading-tight mb-6">{tr.title}</h2>}
        <div className="prose-like text-[#1F1D1A] text-[16px] leading-[1.85]" dangerouslySetInnerHTML={{ __html: tr.html || '' }} />
      </div>
    );
    return (
      <section className="max-w-[1240px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className={`lg:col-span-6 ${mediaFirst ? '' : 'lg:order-2'}`}>{media}</div>
        <div className="lg:col-span-6">{text}</div>
      </section>
    );
  }
  if (block.type === 'image') {
    const isPdf = /\.pdf($|\?)/i.test(tr.image || '');
    return (
      <section className="max-w-[1120px] mx-auto px-5 lg:px-10">
        {tr.title && <h2 className="font-display text-[#1F1D1A] text-[32px] lg:text-[48px] leading-tight mb-6">{tr.title}</h2>}
        {tr.image && (isPdf ? (
          <a href={tr.image} className="pill pill-outline inline-flex" target="_blank" rel="noreferrer">
            PDF oeffnen <ArrowRight className="w-4 h-4" />
          </a>
        ) : (
          <figure>
            <div className="relative rounded-[22px] overflow-hidden aspect-[16/9] bg-white border border-[#E5E0D2]">
              <img src={tr.image} alt={mediaAlt(overlay, tr.image, lang, tr.alt || tr.title || '')} className="absolute inset-0 w-full h-full object-cover" />
            </div>
            {tr.caption && <figcaption className="mt-3 text-[13px] text-[#5C5852]">{tr.caption}</figcaption>}
          </figure>
        ))}
      </section>
    );
  }
  if (block.type === 'cta') {
    return (
      <section className="max-w-[1120px] mx-auto px-5 lg:px-10">
        <div className="rounded-[24px] bg-[#1F1D1A] text-white p-8 lg:p-12">
          {tr.eyebrow && <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F8B37A] font-medium mb-4">{tr.eyebrow}</p>}
          {tr.title && <h2 className="font-display text-[34px] lg:text-[56px] leading-tight max-w-3xl">{tr.title}</h2>}
          {tr.text && <p className="mt-5 text-white/72 text-[17px] leading-relaxed max-w-2xl">{tr.text}</p>}
          {tr.button_text && <Link to={tr.button_link || '/offerte'} className="pill pill-leaf mt-8 inline-flex">{tr.button_text} <ArrowRight className="w-4 h-4" /></Link>}
        </div>
      </section>
    );
  }
  if (block.type === 'faq') {
    const items = Array.isArray(tr.items) ? tr.items : [];
    return (
      <section className="max-w-[980px] mx-auto px-5 lg:px-10">
        {tr.title && <h2 className="font-display text-[#1F1D1A] text-[34px] lg:text-[52px] leading-tight mb-7">{tr.title}</h2>}
        <div className="space-y-3">
          {items.map((item, idx) => (
            <details key={idx} className="rounded-xl bg-white border border-[#E5E0D2] p-5">
              <summary className="cursor-pointer font-semibold text-[#1F1D1A]">{item.q}</summary>
              <p className="mt-3 text-[#5C5852] leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>
    );
  }
  return null;
}

function normalizePath(path) {
  return normalizeCustomPath(path);
}

export function findPublishedCustomPage(overlay, path) {
  const normalizedPath = normalizeCustomPath(path);
  return (overlay?.pages || []).find((p) => p?.published !== false && normalizeCustomPath(p.path || p.slug) === normalizedPath);
}

export function normalizeCustomPath(path) {
  const raw = String(path || '/').trim();
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : '/';
}
